/**
 * Sprite sheets used by the map renderer and the character creator.
 *
 * A {@link TileId} packs a sheet and a cell index into one uint16 so whole tile layers can live
 * in `Uint16Array`s: `id = (sheet << 10) | index`. Sheet ids start at 1, so `0` means "empty".
 */
import { SEVEN_DAWNS_SHEET_COLUMNS, SEVEN_DAWNS_SHEET_ROWS } from './generated';

export const TILE_SIZE = 16;

export const SheetId = {
  town: 1,
  dungeon: 2,
  farm: 3,
  desert: 4,
  characters: 5,
  sevenDawns: 6,
} as const;
export type SheetId = (typeof SheetId)[keyof typeof SheetId];
export type SheetName = keyof typeof SheetId;

export interface SheetDef {
  id: SheetId;
  name: SheetName;
  /** URL relative to the app's base (served from `apps/client/public`). */
  url: string;
  columns: number;
  rows: number;
  /** Pixel step between cells = TILE_SIZE + margin. */
  margin: number;
  pack: string;
  source: string;
}

export const SHEETS: Record<SheetName, SheetDef> = {
  town: {
    id: SheetId.town,
    name: 'town',
    url: 'assets/kenney/tiny-town/tilemap_packed.png',
    columns: 12,
    rows: 11,
    margin: 0,
    pack: 'Tiny Town',
    source: 'https://kenney.nl/assets/tiny-town',
  },
  dungeon: {
    id: SheetId.dungeon,
    name: 'dungeon',
    url: 'assets/kenney/tiny-dungeon/tilemap_packed.png',
    columns: 12,
    rows: 11,
    margin: 0,
    pack: 'Tiny Dungeon',
    source: 'https://kenney.nl/assets/tiny-dungeon',
  },
  farm: {
    id: SheetId.farm,
    name: 'farm',
    url: 'assets/kenney/tiny-farm/tilemap_packed.png',
    columns: 12,
    rows: 11,
    margin: 0,
    pack: 'Tiny Farm',
    source: 'https://kenney.nl/assets/tiny-farm',
  },
  desert: {
    id: SheetId.desert,
    name: 'desert',
    url: 'assets/kenney/desert-shooter-pack/tilemap_packed.png',
    columns: 18,
    rows: 13,
    margin: 0,
    pack: 'Desert Shooter Pack',
    source: 'https://kenney.nl/assets/desert-shooter-pack',
  },
  characters: {
    id: SheetId.characters,
    name: 'characters',
    url: 'assets/kenney/roguelike-characters/roguelikeChar_transparent.png',
    columns: 54,
    rows: 12,
    margin: 1,
    pack: 'Roguelike Characters',
    source: 'https://kenney.nl/assets/roguelike-characters',
  },
  sevenDawns: {
    id: SheetId.sevenDawns,
    name: 'sevenDawns',
    url: 'assets/seven-dawns/tiles.png',
    columns: SEVEN_DAWNS_SHEET_COLUMNS,
    rows: SEVEN_DAWNS_SHEET_ROWS,
    margin: 0,
    pack: 'Seven Dawns hand-pixelled tiles',
    source: 'tools/assets/build_tiles.py',
  },
};

export const SHEET_LIST: readonly SheetDef[] = Object.values(SHEETS);

const SHEET_BY_ID = new Map<number, SheetDef>(SHEET_LIST.map((s) => [s.id, s]));

/** Packed tile reference: `(sheet << 10) | index`. 0 = empty. */
export type TileId = number;
export const EMPTY_TILE: TileId = 0;

export function tileId(sheet: SheetId, index: number): TileId {
  return (sheet << 10) | index;
}

export function tileSheet(id: TileId): SheetDef | undefined {
  return SHEET_BY_ID.get(id >> 10);
}

export function tileIndex(id: TileId): number {
  return id & 0x3ff;
}

/** Source rectangle (in sheet pixels) for a packed tile id. */
export function tileRect(id: TileId): { sheet: SheetDef; sx: number; sy: number } | null {
  const sheet = tileSheet(id);
  if (!sheet) return null;
  const i = tileIndex(id);
  if (i >= sheet.columns * sheet.rows) return null;
  const step = TILE_SIZE + sheet.margin;
  return { sheet, sx: (i % sheet.columns) * step, sy: Math.floor(i / sheet.columns) * step };
}

/** Cell of the characters sheet by column/row (the sheet is organised in column blocks). */
export function charCell(col: number, row: number): TileId {
  return tileId(SheetId.characters, row * SHEETS.characters.columns + col);
}
